import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { z } from "zod";
import { MAX_INVITES } from "@lezz/shared/constants";

import { posthog } from "@/shared/lib/posthog";
import { BackButton } from "@/shared/components/ui/back-button";
import { Button } from "@/shared/components/ui/button";
import { FormInput } from "@/shared/components/ui/form-input";
import { Heading } from "@/shared/components/ui/heading";
import { useMyInvites, useSendInvites } from "@/features/auth/api/invites";

const schema = z.object({
  emails: z
    .array(z.object({ value: z.string().email("E-mail inválido") }))
    .min(1),
});

type FormData = z.infer<typeof schema>;

export default function SendInviteScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { source } = useLocalSearchParams<{ source?: string }>();
  const { data: existing = [], isLoading } = useMyInvites();
  const { mutate: sendInvites, isPending, error: sendError } = useSendInvites();
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const slotsLeft = MAX_INVITES - existing.length;
  const isReadOnly = slotsLeft === 0;

  // Which surface sent the user here — the callouts pass their placement, the
  // settings entry point passes nothing.
  useEffect(() => {
    posthog?.capture("invite_screen_viewed", { source: source ?? "settings" });
    // Once per visit, regardless of param identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { emails: [{ value: "" }] },
  });

  const { fields, append } = useFieldArray({ control, name: "emails" });

  const onSubmit = (data: FormData) => {
    const emails = data.emails
      .map((f) => f.value.trim())
      .filter((e) => e.length > 0);

    if (emails.length === 0) return;

    posthog?.capture("invite_form_submitted", { email_count: emails.length });

    sendInvites(emails, {
      onSuccess: (data) => {
        posthog?.capture("invite_email_sent", {
          count: data.invited,
          slots_left: slotsLeft,
        });
        reset({ emails: [{ value: "" }] });
        setInfoMessage(data.message ?? null);
      },
    });
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <View
          className="border-border flex-row items-center justify-between border-b px-4 pb-4"
          style={{ paddingTop: insets.top || 24 }}
        >
          <BackButton onPress={() => router.back()} />
          <Heading variant="section">Convidar amigas</Heading>
          <View className="h-10 w-10" />
        </View>

        <ScrollView
          className="flex-1"
          contentContainerClassName="px-6 py-6"
          keyboardShouldPersistTaps="handled"
        >
          {isLoading ? null : (
            <>
              {existing.map((invite) => (
                <View key={invite.email} className="mb-4 w-full">
                  <View className="bg-card-event border-b border-white px-4 pt-3 pb-2">
                    <Text className="text-muted-foreground mb-1 text-xs">
                      E-mail
                    </Text>
                    <Text className="text-muted-foreground text-base leading-tight">
                      {invite.email}
                    </Text>
                  </View>
                </View>
              ))}

              {!isReadOnly &&
                fields.map((field, index) => (
                  <FormInput
                    key={field.id}
                    control={control}
                    name={`emails.${index}.value`}
                    label="E-mail"
                    placeholder="amiga@exemplo.com"
                    keyboardType="email-address"
                    autoComplete="email"
                  />
                ))}

              {!isReadOnly && fields.length < slotsLeft && (
                <Pressable
                  onPress={() => append({ value: "" })}
                  className="mb-6 self-start"
                  accessibilityRole="button"
                  accessibilityLabel="Adicionar mais um e-mail"
                >
                  <Text className="text-brand text-sm font-semibold">
                    + Adicionar mais
                  </Text>
                </Pressable>
              )}

              {infoMessage && (
                <Text className="text-muted-foreground mb-4 text-sm">
                  {infoMessage}
                </Text>
              )}

              {sendError && (
                <Text className="text-destructive mb-4 text-sm">
                  {sendError instanceof Error
                    ? sendError.message
                    : "Erro ao enviar convites"}
                </Text>
              )}

              <Button
                size="lg"
                onPress={handleSubmit(onSubmit)}
                loading={isPending}
                disabled={isReadOnly}
              >
                Enviar convites
              </Button>
            </>
          )}
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
